import React, { useCallback, useEffect, useMemo, useState } from "react"
import {
  UserQRAdminView,
  type UserQRAdminCaptureConfig,
  type UserQRAdminCaptureKey,
  type UserQRAdminDraft,
  type UserQRAdminForm,
  type UserQRAdminFormBusyAction,
  type UserQRAdminResponse,
  type UserQRAdminSettingItem,
} from "react-ui-components/components/UserQRAdminView"
import {
  Timestamp,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  setDoc,
  updateDoc,
} from "firebase/firestore"
import { getDownloadURL, ref, uploadBytes } from "firebase/storage"
import { QRCodeCanvas } from "qrcode.react"

import { db, storage } from "../../firebase"
import { buildUserQRPublicUrl, formatUserQRDate } from "./userQRAdminUtils"

const defaultCapture = (): UserQRAdminCaptureConfig => ({
  name: { enabled: true, required: true, label: "Name" },
  phone: { enabled: true, required: true, label: "Phone" },
  address: { enabled: true, required: false, label: "Address" },
  business: { enabled: false, required: false, label: "Business" },
})

const emptyDraft = (): UserQRAdminDraft => ({
  title: "",
  description: "",
  active: true,
  capture: defaultCapture(),
  image: {
    url: "",
    height: 260,
    widthPercent: 100,
    fit: "cover",
    radius: 12,
    align: "center",
  },
  termsHtml: "",
  termsRequired: false,
  redirectDelaySeconds: 3,
  settings: [],
  captureGps: false,
  gpsRequired: false,
})

const UserQRAdmin = () => {
  const [tab, setTab] = useState(0)
  const [forms, setForms] = useState<UserQRAdminForm[]>([])
  const [responses, setResponses] = useState<UserQRAdminResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [presentDialogOpen, setPresentDialogOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [selectedFormId, setSelectedFormId] = useState("")
  const [draft, setDraft] = useState<UserQRAdminDraft>(emptyDraft())
  const [uploading, setUploading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [busyFormAction, setBusyFormAction] = useState<{ formId: string; type: UserQRAdminFormBusyAction } | null>(null)
  const [responseBusyId, setResponseBusyId] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [search, setSearch] = useState("")
  const [flagFilter, setFlagFilter] = useState("")
  const [newFlags, setNewFlags] = useState<Record<string, string>>({})

  const load = useCallback(async () => {
    setLoading(true)
    setErrorMessage(null)
    try {
      const formSnap = await getDocs(query(collection(db, "userQrForms"), orderBy("createdAt", "desc")))
      const nextForms = formSnap.docs.map((item) => ({ id: item.id, ...item.data() } as UserQRAdminForm))
      setForms(nextForms)
      setSelectedFormId((current) => (current && nextForms.some((form) => form.id === current) ? current : nextForms[0]?.id || ""))

      const groups = await Promise.all(
        nextForms.map(async (form) => {
          const snap = await getDocs(collection(db, "userQrForms", form.id, "responses"))
          return snap.docs.map(
            (item) =>
              ({
                id: item.id,
                formId: form.id,
                formTitle: form.title,
                ...item.data(),
              } as UserQRAdminResponse),
          )
        }),
      )
      setResponses(
        groups.flat().sort((a, b) => ((b.submittedAt as any)?.toMillis?.() || 0) - ((a.submittedAt as any)?.toMillis?.() || 0)),
      )
    } catch (error) {
      console.error("Failed to load UserQR data", error)
      setErrorMessage("Unable to load UserQR data. Please try again.")
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const allFlags = useMemo(
    () => Array.from(new Set(responses.flatMap((item) => item.flags || []))).sort(),
    [responses],
  )

  const visibleResponses = useMemo(() => {
    const q = search.trim().toLowerCase()
    return responses.filter((item) => {
      if (flagFilter && !(item.flags || []).includes(flagFilter)) return false
      if (!q) return true
      return (
        (item.formTitle || "").toLowerCase().includes(q) ||
        JSON.stringify(item.answers || {}).toLowerCase().includes(q) ||
        JSON.stringify(item.settings || {}).toLowerCase().includes(q) ||
        (item.flags || []).some((flag) => flag.toLowerCase().includes(q))
      )
    })
  }, [responses, search, flagFilter])

  const selectedForm = useMemo(
    () => forms.find((form) => form.id === selectedFormId) || null,
    [forms, selectedFormId],
  )

  const openNew = useCallback(() => {
    setEditingId(null)
    setDraft(emptyDraft())
    setDialogOpen(true)
  }, [])

  const openEdit = useCallback((form: UserQRAdminForm) => {
    const freshDraft = emptyDraft()
    setEditingId(form.id)
    setDraft({
      ...freshDraft,
      title: form.title,
      description: form.description || "",
      active: form.active !== false,
      capture: { ...defaultCapture(), ...(form.capture || {}) },
      image: { ...freshDraft.image, ...(form.image || {}) },
      termsHtml: form.termsHtml || "",
      termsRequired: Boolean(form.termsRequired),
      redirectDelaySeconds: typeof form.redirectDelaySeconds === "number" ? Math.max(0, form.redirectDelaySeconds) : 3,
      settings: form.settings || [],
      captureGps: Boolean(form.captureGps),
      gpsRequired: Boolean(form.gpsRequired),
    })
    setDialogOpen(true)
  }, [])

  const updateCapture = useCallback(
    (key: UserQRAdminCaptureKey, patch: Partial<UserQRAdminCaptureConfig[UserQRAdminCaptureKey]>) => {
      setDraft((current) => ({
        ...current,
        capture: { ...current.capture, [key]: { ...current.capture[key], ...patch } },
      }))
    },
    [],
  )

  const uploadImage = useCallback(async (file?: File) => {
    if (!file) return
    setUploading(true)
    setErrorMessage(null)
    try {
      const storageRef = ref(storage, `userqr/header-images/${Date.now()}-${file.name}`)
      await uploadBytes(storageRef, file)
      const url = await getDownloadURL(storageRef)
      setDraft((current) => ({ ...current, image: { ...current.image, url } }))
    } catch (error) {
      console.error("Failed to upload UserQR image", error)
      setErrorMessage("Unable to upload the image. Please try again.")
    } finally {
      setUploading(false)
    }
  }, [])

  const addSetting = useCallback(() => {
    setDraft((current) => ({
      ...current,
      settings: [...current.settings, { id: `setting_${Date.now()}`, key: "", value: "" }],
    }))
  }, [])

  const updateSetting = useCallback((index: number, patch: Partial<UserQRAdminSettingItem>) => {
    setDraft((current) => ({
      ...current,
      settings: current.settings.map((item, i) => (i === index ? { ...item, ...patch } : item)),
    }))
  }, [])

  const removeSetting = useCallback((index: number) => {
    setDraft((current) => ({ ...current, settings: current.settings.filter((_, i) => i !== index) }))
  }, [])

  const save = useCallback(async () => {
    if (!draft.title.trim() || saving) return
    setSaving(true)
    setErrorMessage(null)
    try {
      const payload = {
        ...draft,
        title: draft.title.trim(),
        settings: draft.settings.filter((item) => item.key.trim()),
        updatedAt: Timestamp.now(),
      }

      if (editingId) {
        await updateDoc(doc(db, "userQrForms", editingId), payload)
        setForms((current) => current.map((form) => (form.id === editingId ? { ...form, ...payload } : form)))
      } else {
        const refDoc = doc(collection(db, "userQrForms"))
        await setDoc(refDoc, { ...payload, createdAt: Timestamp.now() })
        setSelectedFormId(refDoc.id)
      }
      setDialogOpen(false)
      await load()
    } catch (error) {
      console.error("Failed to save UserQR", error)
      setErrorMessage("Unable to save the UserQR. Please try again.")
    } finally {
      setSaving(false)
    }
  }, [draft, editingId, load, saving])

  const toggleActive = useCallback(
    async (form: UserQRAdminForm) => {
      setBusyFormAction({ formId: form.id, type: "toggle" })
      setErrorMessage(null)
      const nextActive = !form.active
      setForms((current) => current.map((item) => (item.id === form.id ? { ...item, active: nextActive } : item)))
      try {
        await updateDoc(doc(db, "userQrForms", form.id), { active: nextActive, updatedAt: Timestamp.now() })
        await load()
      } catch (error) {
        console.error("Failed to update UserQR status", error)
        setForms((current) => current.map((item) => (item.id === form.id ? { ...item, active: form.active } : item)))
        setErrorMessage("Unable to update the UserQR status. Please try again.")
      } finally {
        setBusyFormAction(null)
      }
    },
    [load],
  )

  const removeForm = useCallback(
    async (form: UserQRAdminForm) => {
      if (!window.confirm(`Delete "${form.title}"?`)) return
      setBusyFormAction({ formId: form.id, type: "delete" })
      setErrorMessage(null)
      const previousForms = forms
      setForms((current) => current.filter((item) => item.id !== form.id))
      setSelectedFormId((current) => (current === form.id ? forms.find((item) => item.id !== form.id)?.id || "" : current))
      try {
        await deleteDoc(doc(db, "userQrForms", form.id))
        await load()
      } catch (error) {
        console.error("Failed to delete UserQR", error)
        setForms(previousForms)
        setErrorMessage("Unable to delete the UserQR. Please try again.")
      } finally {
        setBusyFormAction(null)
      }
    },
    [forms, load],
  )

  const updateResponseFlags = useCallback(
    async (response: UserQRAdminResponse, flags: string[]) => {
      setResponseBusyId(response.id)
      setErrorMessage(null)
      const previousResponses = responses
      setResponses((current) =>
        current.map((item) =>
          item.id === response.id && item.formId === response.formId ? { ...item, flags } : item,
        ),
      )
      try {
        await updateDoc(doc(db, "userQrForms", response.formId, "responses", response.id), { flags })
        await load()
        return true
      } catch (error) {
        console.error("Failed to update UserQR response flags", error)
        setResponses(previousResponses)
        setErrorMessage("Unable to update response flags. Please try again.")
        return false
      } finally {
        setResponseBusyId(null)
      }
    },
    [load, responses],
  )

  const addFlag = useCallback(
    async (response: UserQRAdminResponse) => {
      const flag = (newFlags[response.id] || "").trim()
      if (!flag) return
      const saved = await updateResponseFlags(response, Array.from(new Set([...(response.flags || []), flag])))
      if (saved) {
        setNewFlags((current) => ({ ...current, [response.id]: "" }))
      }
    },
    [newFlags, updateResponseFlags],
  )

  const removeResponseFlag = useCallback(
    (response: UserQRAdminResponse, flag: string) => {
      void updateResponseFlags(response, (response.flags || []).filter((item) => item !== flag))
    },
    [updateResponseFlags],
  )

  const copyLink = useCallback((url: string) => {
    void navigator.clipboard?.writeText(url)
  }, [])

  return (
    <UserQRAdminView
      allFlags={allFlags}
      busyFormAction={busyFormAction}
      dialogOpen={dialogOpen}
      draft={draft}
      editingId={editingId}
      errorMessage={errorMessage}
      flagFilter={flagFilter}
      formatDate={formatUserQRDate}
      forms={forms}
      loading={loading}
      newFlags={newFlags}
      onAddFlag={(response) => void addFlag(response)}
      onAddSetting={addSetting}
      onCloseDialog={() => setDialogOpen(false)}
      onClosePresentDialog={() => setPresentDialogOpen(false)}
      onCopyLink={copyLink}
      onDraftChange={setDraft}
      onFlagFilterChange={setFlagFilter}
      onNewFlagChange={(responseId, value) => setNewFlags((current) => ({ ...current, [responseId]: value }))}
      onOpenEdit={openEdit}
      onOpenNew={openNew}
      onPresentDialogOpenChange={setPresentDialogOpen}
      onRemoveForm={(form) => void removeForm(form)}
      onRemoveResponseFlag={removeResponseFlag}
      onRemoveSetting={removeSetting}
      onSave={() => void save()}
      onSearchChange={setSearch}
      onSelectedFormIdChange={setSelectedFormId}
      onTabChange={setTab}
      onToggleActive={(form) => void toggleActive(form)}
      onUpdateCapture={updateCapture}
      onUpdateSetting={updateSetting}
      onUploadImage={(file) => void uploadImage(file)}
      presentDialogOpen={presentDialogOpen}
      publicUrl={buildUserQRPublicUrl}
      renderQrCode={(url, size) => <QRCodeCanvas value={url} size={size} level="M" />}
      responseBusyId={responseBusyId}
      responses={responses}
      saving={saving}
      search={search}
      selectedForm={selectedForm}
      selectedFormId={selectedFormId}
      tab={tab}
      uploading={uploading}
      visibleResponses={visibleResponses}
    />
  )
}

export default UserQRAdmin
